// ============================================
// OfflineIndicator — UKA-X01 D9
//
// Fixed bottom bar displaying offline / sync state.
// Hidden when online and queue is empty (returns null).
//
// Three states:
//   1. Offline + queued items  → amber — "Offline — N verdict(s) queued."
//   2. Online + draining       → blue  — "Syncing N verdict(s)…"
//   3. Online + depth > 0      → amber — "N verdict(s) pending." + "Sync Now" button
//
// Uses useOfflineQueue hook for all state.
// ============================================

import React from 'react';
import { useOfflineQueue } from '../hooks/useOfflineQueue';

// ── Helpers ───────────────────────────────────────────────────────────────────

function pluralize(n: number, singular: string, plural: string): string {
  return n === 1 ? singular : plural;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function OfflineIndicator() {
  const { queueDepth, isOnline, isDraining, drain } = useOfflineQueue();

  // Hidden when fully synced and online
  if (isOnline && queueDepth === 0) {
    return null;
  }

  // ── Determine state ───────────────────────────────────────────────────────

  let backgroundColor: string;
  let borderColor: string;
  let textColor: string;
  let message: string;
  let showSyncButton: boolean;

  if (!isOnline) {
    // Offline with queued items (or just went offline with 0 — still show)
    backgroundColor = '#FFFBEB';
    borderColor = '#FCD34D';
    textColor = '#92400E';
    message =
      queueDepth > 0
        ? `Offline — ${queueDepth} ${pluralize(queueDepth, 'verdict', 'verdicts')} queued. Will sync when reconnected.`
        : 'Offline — verdicts will be queued until connection is restored.';
    showSyncButton = false;
  } else if (isDraining) {
    // Online + actively draining
    backgroundColor = '#EFF6FF';
    borderColor = '#BFDBFE';
    textColor = '#1D4ED8';
    message = `Syncing ${queueDepth} ${pluralize(queueDepth, 'verdict', 'verdicts')}…`;
    showSyncButton = false;
  } else {
    // Online + has depth + not draining (waiting or after partial failure)
    backgroundColor = '#FFFBEB';
    borderColor = '#FCD34D';
    textColor = '#92400E';
    message = `${queueDepth} ${pluralize(queueDepth, 'verdict', 'verdicts')} pending sync.`;
    showSyncButton = true;
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 60,
        backgroundColor,
        borderTop: `1px solid ${borderColor}`,
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        fontFamily: 'DM Sans, system-ui, sans-serif',
        minHeight: '48px',
      }}
      role="status"
      aria-live="polite"
      aria-label="Offline sync status"
    >
      <span
        style={{
          fontSize: '14px',
          fontWeight: 500,
          color: textColor,
          flex: 1,
        }}
      >
        {message}
      </span>

      {showSyncButton && (
        <button
          onClick={drain}
          style={{
            fontSize: '14px',
            fontFamily: 'DM Sans, system-ui, sans-serif',
            fontWeight: 600,
            color: '#FFFFFF',
            backgroundColor: '#D97706',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 16px',
            cursor: 'pointer',
            minHeight: '36px',
            minWidth: '88px',
            flexShrink: 0,
          }}
          aria-label={`Sync ${queueDepth} queued ${pluralize(queueDepth, 'verdict', 'verdicts')} now`}
        >
          Sync Now
        </button>
      )}
    </div>
  );
}
